import { Button, Layout, Menu, theme } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { localStorageGet, localStorageSet } from '@/core/cookie';
import { useThemeMode } from '@/contexts/ThemeContext';
import { get, post } from '@/core/request';
import { workspaceMedProjectMembersPath, workspaceMedProjectModulesPath, workspaceMedProjectNoticesPath, workspaceMedProjectQuickEntriesPath, workspaceMedProjectSettingPath } from './medProjectWorkspacePaths';
import { useEffectiveMenuItems } from './useEffectiveMenuItems';

const { Sider } = Layout;

const innerStyle = { display: 'flex', flexDirection: 'column', height: '100%' };
const logoBaseStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    height: 56,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    transition: 'padding .2s',
    flexShrink: 0,
};
const menuScrollBaseStyle = { flex: 1 };
const menuStyle = { borderRight: 'none', fontSize: 16 };

function getRouteOpenKeys(pathname) {
    if (pathname === '/' || pathname === '') return [];
    if (pathname.startsWith('/medproject')) return ['medproject'];
    if (pathname.startsWith('/invitation')) return ['invitation'];
    if (pathname.startsWith('/conf')) return ['conf'];
    if (pathname.startsWith('/selection')) return ['selection'];
    if (pathname.startsWith('/wj')) return ['wj'];
    if (pathname.startsWith('/healthclass')) return ['healthclass'];
    if (pathname.startsWith('/kb')) return ['kb', 'kb-rearch'];
    if (pathname.startsWith('/healthaccount')) return ['healthaccount', 'eha-enterprises'];
    if (pathname.startsWith('/enterprise') || pathname.startsWith('/my')) return ['enterprise', 'enterprise-staffs', 'enterprise-roles', 'operation-log'];
    if (pathname.startsWith('/uc')) return ['uc', 'user-data', 'uc-mdm', 'consultations-data'];
    if (pathname.startsWith('/operation')) return ['operation', 'operation-source', 'operation-data'];
    if (pathname.startsWith('/finance')) return ['finance'];
    if (pathname.startsWith('/dc')) return ['dc', 'expert-data', 'dc-organization'];
    return [];
}

function getOpenKeysFromMenuItems(items, targetPath) {
    for (const item of items) {
        if (!item.children) continue;
        for (const child of item.children) {
            if (child.key === targetPath) {
                return [item.key];
            }
            if (!child.children) continue;
            for (const grandChild of child.children) {
                if (grandChild.key === targetPath) {
                    return [item.key, child.key];
                }
                if (!grandChild.children) continue;
                for (const deep of grandChild.children) {
                    if (deep.key === targetPath) {
                        return [item.key, child.key, grandChild.key];
                    }
                }
            }
        }
    }
    return [];
}

function findMenuItemByKey(items, key) {
    for (const item of items) {
        if (item.key === key) return item;
        if (item.children) {
            const found = findMenuItemByKey(item.children, key);
            if (found) return found;
        }
    }
    return null;
}

/** 收集某菜单项子树中所有可作为 openKeys 的 key */
function collectKeysInSubtree(item) {
    if (!item) return [];
    if (!item.children?.length) return [item.key];
    const keys = [item.key];
    for (const child of item.children) {
        keys.push(...collectKeysInSubtree(child));
    }
    return keys;
}

/** 收集某菜单项下所有仍含 children 的子级 key（用于展开时同步打开多级子菜单） */
function collectNestedSubmenuKeys(item) {
    if (!item?.children) return [];
    const keys = [];
    for (const child of item.children) {
        if (child.children?.length) {
            keys.push(child.key);
            keys.push(...collectNestedSubmenuKeys(child));
        }
    }
    return keys;
}

export default function AppSider({ collapsed }) {
    const { token } = theme.useToken();
    const { resolvedMode } = useThemeMode();
    const menuTheme = resolvedMode === 'dark' ? 'dark' : 'light';
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const params = useParams();

    const [openKeys, setOpenKeys] = useState(() => getRouteOpenKeys(pathname));

    const menuRef = useRef(null);
    const [hasOverflow, setHasOverflow] = useState(false);
    const overflowCheckKey = useMemo(() => `${collapsed ? '1' : '0'}:${openKeys.join(',')}`, [collapsed, openKeys]);

    const updateOverflowState = useCallback(el => {
        setHasOverflow(prev => {
            const next = el.scrollHeight > el.clientHeight;
            return prev === next ? prev : next;
        });
    }, []);

    useEffect(() => {
        const el = menuRef.current;
        if (!el) return;
        const check = () => updateOverflowState(el);
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        if (el.firstElementChild) ro.observe(el.firstElementChild);
        return () => ro.disconnect();
    }, [updateOverflowState]);

    useEffect(() => {
        const el = menuRef.current;
        if (!el) return;
        void overflowCheckKey;
        const id = window.setTimeout(() => updateOverflowState(el), 300);
        return () => window.clearTimeout(id);
    }, [overflowCheckKey, updateOverflowState]);

    const [subMenuList, setSubMenuList] = useState([]);
    const [healthclassSubMenuList, setHealthclassSubMenuList] = useState(() => {
        try {
            const raw = localStorageGet('healthclassPermissionsList');
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [healthclassOrgCount, setHealthclassOrgCount] = useState(0);

    const effectiveMenuItems = useEffectiveMenuItems({ pathname, subMenuList, healthclassSubMenuList, healthclassOrgCount });

    const topLevelModuleKeys = useMemo(() => effectiveMenuItems.filter(item => item.children?.length).map(item => item.key), [effectiveMenuItems]);

    useEffect(() => {
        if (!collapsed) {
            const keys = getOpenKeysFromMenuItems(effectiveMenuItems, pathname);
            setOpenKeys(keys.length > 0 ? keys : getRouteOpenKeys(pathname));
        }
    }, [pathname, collapsed, effectiveMenuItems]);

    // 模块级鉴权：进入 healthclass/uc/operation 时调用 modular-login，刷新 token/infos/permissionsList
    useEffect(() => {
        const MODULAR_LOGIN_MAP = {
            healthclass: 'HEALTH',
            uc: 'USER',
            operation: 'CHANNEL',
        };
        const module = Object.keys(MODULAR_LOGIN_MAP).find(m => pathname.startsWith(`/${m}`));
        if (module) {
            post('/enterprise/staffs/modular-login', { modulesCode: MODULAR_LOGIN_MAP[module], userName: '' }).then(res => {
                if (res && res.status_code === 200) {
                    localStorage.removeItem('permissionsList');
                    localStorage.removeItem('infos');
                    localStorageSet('token', res.data.token);
                    localStorageSet('infos', JSON.stringify(res.data.infos));
                    localStorageSet('permissionsList', JSON.stringify(res.data.permissionsList));
                    setSubMenuList(res.data.permissionsList);
                    if (module === 'healthclass') {
                        setHealthclassSubMenuList(res.data.permissionsList);
                        localStorageSet('healthclassPermissionsList', JSON.stringify(res.data.permissionsList));
                    }
                }
            });
        } else {
            // 非模块化登录路径：从缓存读取
            const raw = localStorageGet('permissionsList');
            if (raw) {
                try {
                    setSubMenuList(JSON.parse(raw));
                } catch {
                    // format error, ignore
                }
            }
        }
        if (pathname.startsWith('/healthclass')) {
            get('/healthclass/boss/organizations/queryByCount', {}).then(res => {
                if (res && res.status_code === 200) setHealthclassOrgCount(res.data);
            });
        }
    }, [pathname]);

    const [activitiesModuleId, setActivitiesModuleId] = useState(0);
    // `/medproject/projects/new` 已改由 WorkspaceLayout 承载，不再在 MainLayout 侧栏走详情分支
    const isMedProjectDetail = pathname !== '/medproject/projects' && pathname !== '/medproject/projects/new' && pathname.startsWith('/medproject/projects/');
    const isMedConnectDetail = pathname !== '/medconnect/projects' && pathname.startsWith('/medconnect/projects/');

    useEffect(() => {
        const projectPk = params?.projectId ?? params?.id;
        if (!projectPk || !isMedProjectDetail) {
            setActivitiesModuleId(0);
            return;
        }

        setActivitiesModuleId(0);

        moduleListApi({ projectId: projectPk }).then(res => {
            if (res && res.status_code === 200 && res.data) {
                const activitiesId = res.data.find(item => item.moduleType === 'activity')?.id;
                if (activitiesId) setActivitiesModuleId(activitiesId);
            }
        });
    }, [params?.projectId, params?.id, isMedProjectDetail]);

    const selectedKeys = useMemo(() => {
        if (pathname.startsWith('/invitation')) {
            return ['invitation', pathname];
        }
        return [pathname];
    }, [pathname]);

    const handleMenuClick = useCallback(({ key }) => navigate(key), [navigate]);

    const handleOpenChange = useCallback(
        nextKeys => {
            setOpenKeys(prevKeys => {
                const opened = nextKeys.filter(k => !prevKeys.includes(k));
                const closed = prevKeys.filter(k => !nextKeys.includes(k));
                let result = [...nextKeys];

                const newlyOpenedTopLevel = opened.find(k => topLevelModuleKeys.includes(k));
                if (newlyOpenedTopLevel) {
                    const allowedKeys = new Set(collectKeysInSubtree(findMenuItemByKey(effectiveMenuItems, newlyOpenedTopLevel)));
                    result = result.filter(k => allowedKeys.has(k));
                }

                for (const key of opened) {
                    const nestedKeys = collectNestedSubmenuKeys(findMenuItemByKey(effectiveMenuItems, key));
                    result = [...new Set([...result, ...nestedKeys])];
                }
                for (const key of closed) {
                    const nestedKeys = collectNestedSubmenuKeys(findMenuItemByKey(effectiveMenuItems, key));
                    result = result.filter(k => !nestedKeys.includes(k));
                }
                return result;
            });
        },
        [effectiveMenuItems, topLevelModuleKeys],
    );

    const logoStyle = useMemo(
        () => ({
            ...logoBaseStyle,
            justifyContent: 'center',
            borderBottom: `1px solid ${token.colorSplit}`,
        }),
        [collapsed, token.colorSplit],
    );

    const siderStyle = useMemo(
        () => ({
            background: token.colorBgContainer,
            borderRight: `1px solid ${token.colorSplit}`,
            position: 'sticky',
            top: 0,
            height: '100vh',
        }),
        [token.colorBgContainer, token.colorSplit],
    );

    if (isMedProjectDetail) {
        const projectId = params?.projectId ?? params?.id;
        const medProjectItems = [
            { key: workspaceMedProjectModulesPath(projectId, activitiesModuleId), label: <Link to={workspaceMedProjectModulesPath(projectId, activitiesModuleId)}>模块管理</Link> },
            { key: workspaceMedProjectQuickEntriesPath(projectId), label: <Link to={workspaceMedProjectQuickEntriesPath(projectId)}>快捷入口</Link> },
            { key: workspaceMedProjectMembersPath(projectId), label: <Link to={workspaceMedProjectMembersPath(projectId)}>成员管理</Link> },
            { key: workspaceMedProjectNoticesPath(projectId), label: <Link to={workspaceMedProjectNoticesPath(projectId)}>通知管理</Link> },
            { key: `/medproject/projects/${projectId}/info`, label: <Link to={`/medproject/projects/${projectId}/info`}>项目信息</Link> },
            { key: workspaceMedProjectSettingPath(projectId), label: <Link to={workspaceMedProjectSettingPath(projectId)}>页面设置</Link> },
        ];
        return (
            <Sider id="app-sider" collapsed={collapsed} collapsedWidth={48} width={200} style={siderStyle}>
                <div style={innerStyle}>
                    <div id="app-sider-brand" style={logoStyle}>
                        <img src="/logo.svg" alt="logo" style={{ width: 24, height: 24, flexShrink: 0 }} />
                        {!collapsed && <span style={{ fontWeight: 700, fontSize: 18, color: token.colorTextHeading }}>华媒康讯•智管平台</span>}
                    </div>
                    <div ref={menuRef} style={{ ...menuScrollBaseStyle, overflowY: 'auto', overflowX: 'hidden' }}>
                        <Menu theme={menuTheme} mode="inline" inlineCollapsed={collapsed} selectedKeys={selectedKeys} items={medProjectItems} style={menuStyle} />
                    </div>
                </div>
            </Sider>
        );
    }

    if (isMedConnectDetail) {
        const connId = params?.id;
        const medConnectItems = [
            { key: `/medconnect/projects/${connId}`, label: <Link to={`/medconnect/projects/${connId}`}>项目配置</Link> },
            {
                key: `/medconnect/projects/${connId}/list`,
                label: '问答管理',
                children: [
                    { key: `/medconnect/projects/${connId}/question-list`, label: <Link to={`/medconnect/projects/${connId}/question-list`}>问题管理</Link> },
                    { key: `/medconnect/projects/${connId}/answer-list`, label: <Link to={`/medconnect/projects/${connId}/answer-list`}>回答管理</Link> },
                ],
            },
            { key: `/medconnect/projects/${connId}/users`, label: <Link to={`/medconnect/projects/${connId}/users`}>人员管理</Link> },
            { key: `/medconnect/projects/${connId}/labfee/batches`, label: <Link to={`/medconnect/projects/${connId}/labfee/batches`}>劳务管理</Link> },
        ];
        return (
            <Sider id="app-sider" collapsed={collapsed} collapsedWidth={48} width={200} style={siderStyle}>
                <div style={innerStyle}>
                    <div id="app-sider-brand" style={logoStyle}>
                        <img src="/logo.svg" alt="logo" style={{ width: 24, height: 24, flexShrink: 0 }} />
                        {!collapsed && <span style={{ fontWeight: 700, fontSize: 18, color: token.colorTextHeading }}>华媒康讯•智管平台</span>}
                    </div>
                    <div ref={menuRef} style={{ ...menuScrollBaseStyle, overflowY: 'auto', overflowX: 'hidden' }}>
                        <Menu theme={menuTheme} mode="inline" inlineCollapsed={collapsed} selectedKeys={selectedKeys} defaultOpenKeys={[`/medconnect/projects/${connId}/list`]} items={medConnectItems} style={menuStyle} />
                    </div>
                </div>
            </Sider>
        );
    }

    return (
        <Sider id="app-sider" collapsed={collapsed} collapsedWidth={48} width={200} style={siderStyle}>
            <div style={innerStyle}>
                <div id="app-sider-brand" style={logoStyle}>
                    <img src="/logo.svg" alt="logo" style={{ width: 24, height: 24, flexShrink: 0 }} />
                    {!collapsed && <span style={{ fontWeight: 700, fontSize: 18, color: token.colorTextHeading }}>华媒康讯•智管平台</span>}
                </div>

                <div
                    ref={menuRef}
                    style={{
                        ...menuScrollBaseStyle,
                        ...(collapsed ? { overflow: 'visible' } : { overflowY: 'auto', overflowX: 'hidden' }),
                    }}
                >
                    <Menu
                        rootClassName="app-sider-module-menu-root"
                        theme={menuTheme}
                        mode="inline"
                        inlineCollapsed={collapsed}
                        selectedKeys={selectedKeys}
                        {...(collapsed ? {} : { openKeys, onOpenChange: handleOpenChange })}
                        items={effectiveMenuItems}
                        onClick={handleMenuClick}
                        style={menuStyle}
                        getPopupContainer={() => document.body}
                    />
                </div>

                {!collapsed && hasOverflow && (
                    <div style={{ borderTop: `1px solid ${token.colorSplit}`, padding: '8px 12px', flexShrink: 0 }}>
                        <Button type="text" size="small" icon={<i className="bi bi-arrows-collapse" />} onClick={() => setOpenKeys([])} style={{ color: token.colorTextDescription, width: '100%', textAlign: 'left' }} aria-label="全部收起菜单分组">
                            全部收起
                        </Button>
                    </div>
                )}
            </div>
        </Sider>
    );
}
